/* ═══════════════════════════════════════════════════════
   FroGames Arena — Shared JS
   Handles: league pages + hub page
   Config read from data-* attributes (no Liquid)
   ═══════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── Util ── */
  var parseList = function (s) { return (s || '').split('||').map(function (x) { return (x || '').trim(); }).filter(Boolean); };
  var cleanKey = function (s) { return (s || '').trim().toLowerCase(); };
  var fmtPct = function (x) { return Math.round(x).toString() + '%'; };
  var fmtNum = function (x) { return (Math.round(x * 100) / 100).toFixed(2).replace('.', ','); };
  var entriesSorted = function (map) {
    return Object.entries(map).filter(function (e) { return e[0] && e[0].trim().length; })
      .sort(function (a, b) { return b[1] - a[1] || a[0].localeCompare(b[0], 'it'); });
  };
  var escAttr = function (s) { return (s || '').replace(/"/g, '&quot;').replace(/</g, '&lt;'); };

  function buildAlias(raw) {
    var alias = {};
    (raw || '').split('\n').forEach(function (line) {
      var l = line.trim();
      if (!l) return;
      var parts = l.split('=');
      if (parts.length < 2) return;
      var a = parts[0].trim(), c = parts.slice(1).join('=').trim();
      if (a && c) alias[cleanKey(a)] = c;
    });
    return alias;
  }

  function makeCanon(alias) {
    return function (name) {
      var raw = (name || '').trim();
      if (!raw) return '';
      return (alias[cleanKey(raw)] || raw).trim();
    };
  }

  /* ── Data Layer ── */
  function parseTournaments(root, canon, pts, league, isGlobal) {
    var cardsAll = Array.from(root.querySelectorAll('[data-arena-tournament]'));
    if (!isGlobal) {
      cardsAll = cardsAll.filter(function (el) { return (el.dataset.league || '').toLowerCase() === league; });
    }
    return cardsAll.map(function (el) {
      var winner = canon(el.dataset.winner);
      var finalist = canon(el.dataset.finalist);
      var semiList = parseList(el.dataset.semi).map(canon);
      var qList = parseList(el.dataset.quarti).map(canon);
      var oList = parseList(el.dataset.ottavi).map(canon);
      var game = (el.dataset.game || '').trim();
      var date = (el.dataset.date || '').trim();
      var title = (el.dataset.title || '').trim();
      var lg = (el.dataset.league || '').trim();

      var partSet = new Set([winner, finalist].concat(semiList, qList, oList).filter(Boolean));

      var placementOf = function (p) {
        if (p === winner) return { tag: '🥇', label: '1°', pts: pts.w, inPoints: true, top8: true, isWin: true, isFinal: true };
        if (p === finalist) return { tag: '🥈', label: '2°', pts: pts.f, inPoints: true, top8: true, isWin: false, isFinal: true };
        if (semiList.includes(p)) return { tag: '🥉', label: 'Top 3–4', pts: pts.s, inPoints: true, top8: true, isWin: false, isFinal: false };
        if (qList.includes(p)) return { tag: '🏅', label: 'Top 5–8', pts: pts.q, inPoints: true, top8: true, isWin: false, isFinal: false };
        if (oList.includes(p)) return { tag: '🎲', label: 'A punti', pts: pts.o, inPoints: true, top8: false, isWin: false, isFinal: false };
        return null;
      };

      return { el: el, date: date, title: title, game: game, league: lg, winner: winner, finalist: finalist, semiList: semiList, qList: qList, oList: oList, partSet: partSet, placementOf: placementOf };
    });
  }

  function buildProfiles(items, canon, pts) {
    var pointsTotal = {}, wins = {}, finals = {}, inPoints = {}, top8 = {};
    var profile = {};
    var gameIndex = {}, gamesSet = new Set();

    var add = function (map, name, delta) {
      var k = canon(name);
      if (!k) return;
      map[k] = (map[k] || 0) + delta;
    };

    var ensure = function (p) {
      var n = canon(p);
      if (!n) return null;
      if (!profile[n]) profile[n] = { name: n, points: 0, wins: 0, finals: 0, inPoints: 0, top8: 0, games: {}, results: [] };
      return profile[n];
    };

    var addGame = function (game, name, ptsAdd, isWin, isFinal) {
      if (!game) return;
      var p = canon(name);
      if (!p) return;
      gamesSet.add(game);
      if (!gameIndex[game]) gameIndex[game] = { points: {}, wins: {}, finals: {} };
      var g = gameIndex[game];
      g.points[p] = (g.points[p] || 0) + ptsAdd;
      if (isWin) g.wins[p] = (g.wins[p] || 0) + 1;
      if (isFinal) g.finals[p] = (g.finals[p] || 0) + 1;
    };

    items.forEach(function (it) {
      add(pointsTotal, it.winner, pts.w);
      add(pointsTotal, it.finalist, pts.f);
      it.semiList.forEach(function (n) { add(pointsTotal, n, pts.s); });
      it.qList.forEach(function (n) { add(pointsTotal, n, pts.q); });
      it.oList.forEach(function (n) { add(pointsTotal, n, pts.o); });

      add(wins, it.winner, 1);
      add(finals, it.winner, 1);
      add(finals, it.finalist, 1);

      it.partSet.forEach(function (p) {
        var pr = ensure(p);
        if (!pr) return;
        var pl = it.placementOf(pr.name);
        if (!pl) return;

        pr.points += pl.pts;
        pr.inPoints += 1;
        add(inPoints, pr.name, 1);

        if (pl.top8) { pr.top8 += 1; add(top8, pr.name, 1); }
        if (pl.isWin) pr.wins += 1;
        if (pl.isFinal) pr.finals += 1;

        if (it.game) {
          if (!pr.games[it.game]) pr.games[it.game] = { points: 0, wins: 0, finals: 0, inPoints: 0, top8: 0 };
          var pg = pr.games[it.game];
          pg.points += pl.pts;
          pg.inPoints += 1;
          if (pl.top8) pg.top8 += 1;
          if (pl.isWin) pg.wins += 1;
          if (pl.isFinal) pg.finals += 1;
        }

        pr.results.push({ date: it.date, title: it.title, game: it.game, tag: pl.tag, label: pl.label, pts: pl.pts });
      });

      addGame(it.game, it.winner, pts.w, true, true);
      addGame(it.game, it.finalist, pts.f, false, true);
      it.semiList.forEach(function (n) { addGame(it.game, n, pts.s, false, false); });
      it.qList.forEach(function (n) { addGame(it.game, n, pts.q, false, false); });
      it.oList.forEach(function (n) { addGame(it.game, n, pts.o, false, false); });
    });

    return { pointsTotal: pointsTotal, wins: wins, finals: finals, inPoints: inPoints, top8: top8, profile: profile, gameIndex: gameIndex, gamesSet: gamesSet };
  }

  /* ── Render helpers ── */
  function playerBtn(name, strong) {
    var safe = escAttr(name);
    var inner = strong ? '<strong>' + name + '</strong>' : name;
    return '<button type="button" class="arena__plink" data-arena-player="' + safe + '">' + inner + '</button>';
  }

  function topTies(map, limitNames) {
    limitNames = limitNames || 3;
    var sorted = entriesSorted(map);
    if (!sorted.length) return { names: '—', val: '—', raw: null };
    var best = sorted[0][1];
    var tied = sorted.filter(function (x) { return x[1] === best; }).map(function (x) { return x[0]; });
    var shown = tied.slice(0, limitNames).map(function (n) { return playerBtn(n, false); }).join(', ');
    var more = tied.length > limitNames ? ' <span class="arena__more">+' + (tied.length - limitNames) + '</span>' : '';
    return { names: shown + more, val: String(best), raw: best };
  }

  /* ── Render: Podium ── */
  function renderPodium(root, totalSorted) {
    var podium = root.querySelector('[data-arena-podium]');
    if (!podium) return;
    podium.innerHTML = '';
    var top3 = totalSorted.slice(0, 3);
    var medals = ['🥇', '🥈', '🥉'];

    top3.forEach(function (entry, i) {
      var name = entry[0], pts = entry[1];
      var step = document.createElement('div');
      step.className = 'arena__podStep arena__podStep--' + (i + 1);
      step.innerHTML =
        '<div class="arena__podMedal">' + medals[i] + '</div>' +
        '<button type="button" class="arena__podName" data-arena-player="' + escAttr(name) + '">' + name + '</button>' +
        '<div class="arena__podPts">' + pts + ' pt</div>' +
        '<div class="arena__podBar arena__podBar--' + (i + 1) + '"></div>';
      podium.appendChild(step);
    });

    setTimeout(function () { podium.classList.add('is-animated'); }, 100);
  }

  /* ── Render: Leaderboard ── */
  function renderLeaderboard(root, totalSorted, maxPts) {
    var body = root.querySelector('[data-arena-body]');
    if (!body) return;
    body.innerHTML = '';
    var last = null, rank = 0;
    totalSorted.forEach(function (entry, idx) {
      var name = entry[0], p = entry[1];
      if (p !== last) rank = idx + 1;
      last = p;
      var posClass = 'arena__pos';
      if (rank <= 3) posClass += ' arena__pos--' + rank;
      var barW = maxPts > 0 ? Math.max(4, Math.round((p / maxPts) * 100)) : 0;
      var tr = document.createElement('tr');
      tr.innerHTML =
        '<td><span class="' + posClass + '">' + rank + '</span></td>' +
        '<td>' + playerBtn(name, rank === 1) + '</td>' +
        '<td>' + (rank === 1 ? '<strong>' + p + '</strong>' : p) +
        ' <span class="arena__miniBar" style="width:' + barW + 'px"></span></td>';
      body.appendChild(tr);
    });
  }

  /* ── Render: Stats ── */
  function renderStats(root, data, cfg) {
    var setStat = function (key, nameHtml, valHtml, subHtml) {
      var box = root.querySelector('[data-arena-stat="' + key + '"]');
      if (!box) return;
      var n = box.querySelector('.arena__statName');
      var v = box.querySelector('.arena__statVal');
      var s = box.querySelector('.arena__statSub');
      if (n) n.innerHTML = nameHtml;
      if (v) v.innerHTML = valHtml;
      if (s && subHtml) s.innerHTML = subHtml;
    };

    setStat('wins', topTies(data.wins).names, '<strong>' + topTies(data.wins).val + '</strong>', 'Tornei vinti');
    setStat('finals', topTies(data.finals).names, '<strong>' + topTies(data.finals).val + '</strong>', 'Finali (1° o 2°)');
    setStat('inpoints', topTies(data.inPoints).names, '<strong>' + topTies(data.inPoints).val + '</strong>', 'Ingressi a punti (ottavi o meglio)');
    setStat('top8', topTies(data.top8).names, '<strong>' + topTies(data.top8).val + '</strong>', 'Top 8 (quarti o meglio)');

    var inSorted = entriesSorted(data.inPoints);

    var bestWR = null;
    inSorted.forEach(function (entry) {
      var name = entry[0], ip = entry[1];
      if (ip < cfg.minInPoints) return;
      var w = data.wins[name] || 0;
      var r = ip > 0 ? (w / ip) : 0;
      if (bestWR === null || r > bestWR.r || (r === bestWR.r && ip > bestWR.ip)) bestWR = { name: name, r: r, w: w, ip: ip };
    });
    if (bestWR) {
      setStat('winrate_inpoints', playerBtn(bestWR.name, false), '<strong>' + fmtPct(bestWR.r * 100) + '</strong>', 'Vittorie/A punti: <strong>' + bestWR.w + '</strong>/<strong>' + bestWR.ip + '</strong>');
    } else {
      setStat('winrate_inpoints', '—', '—', 'Min ' + cfg.minInPoints + ' ingressi a punti');
    }

    var bestAdv = null;
    inSorted.forEach(function (entry) {
      var name = entry[0], ip = entry[1];
      if (ip < cfg.minInPoints) return;
      var t8 = data.top8[name] || 0;
      var r = ip > 0 ? (t8 / ip) : 0;
      if (bestAdv === null || r > bestAdv.r || (r === bestAdv.r && ip > bestAdv.ip)) bestAdv = { name: name, r: r, t8: t8, ip: ip };
    });
    if (bestAdv) {
      setStat('advance_rate', playerBtn(bestAdv.name, false), '<strong>' + fmtPct(bestAdv.r * 100) + '</strong>', 'Top 8/A punti: <strong>' + bestAdv.t8 + '</strong>/<strong>' + bestAdv.ip + '</strong>');
    } else {
      setStat('advance_rate', '—', '—', 'Min ' + cfg.minInPoints + ' ingressi a punti');
    }

    var bestAvg = null;
    inSorted.forEach(function (entry) {
      var name = entry[0], ip = entry[1];
      var p = data.pointsTotal[name] || 0;
      var avg = ip > 0 ? (p / ip) : 0;
      if (bestAvg === null || avg > bestAvg.avg || (avg === bestAvg.avg && p > bestAvg.p)) bestAvg = { name: name, avg: avg, p: p, ip: ip };
    });
    if (bestAvg) {
      setStat('avg_points', playerBtn(bestAvg.name, false), '<strong>' + fmtNum(bestAvg.avg) + '</strong>', 'Totale <strong>' + bestAvg.p + '</strong> pt in <strong>' + bestAvg.ip + '</strong> ingressi a punti');
    } else {
      setStat('avg_points', '—', '—', 'Media punti');
    }

    var recentPoints = {};
    var itemsSortedByDate = cfg._items.slice().sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); });
    var recentSlice = itemsSortedByDate.slice(0, Math.max(1, cfg.recentN));
    var addR = function (map, name, delta) { var k = cfg._canon(name); if (!k) return; map[k] = (map[k] || 0) + delta; };
    recentSlice.forEach(function (it) {
      addR(recentPoints, it.winner, cfg._pts.w);
      addR(recentPoints, it.finalist, cfg._pts.f);
      it.semiList.forEach(function (n) { addR(recentPoints, n, cfg._pts.s); });
      it.qList.forEach(function (n) { addR(recentPoints, n, cfg._pts.q); });
      it.oList.forEach(function (n) { addR(recentPoints, n, cfg._pts.o); });
    });
    var rp = entriesSorted(recentPoints);
    if (rp.length) {
      setStat('hot_recent', playerBtn(rp[0][0], false), '<strong>' + rp[0][1] + '</strong>', 'Ultimi ' + cfg.recentN + ' tornei');
    } else {
      setStat('hot_recent', '—', '—', 'Ultimi ' + cfg.recentN + ' tornei');
    }
  }

  /* ── Render: Game Stats ── */
  function renderGameStats(root, data) {
    var sel = root.querySelector('[data-arena-game-select]');
    var bodyG = root.querySelector('[data-arena-game-body]');
    var hint = root.querySelector('[data-arena-game-hint]');
    var boxWins = root.querySelector('[data-arena-game-wins]');
    var boxPts = root.querySelector('[data-arena-game-points]');
    if (!sel) return;

    var games = Array.from(data.gamesSet).sort(function (a, b) { return a.localeCompare(b, 'it'); });
    games.forEach(function (g) {
      var opt = document.createElement('option');
      opt.value = g;
      opt.textContent = g;
      sel.appendChild(opt);
    });

    sel.addEventListener('change', function () {
      var gName = sel.value;
      if (!bodyG || !boxWins || !boxPts) return;
      bodyG.innerHTML = '';
      if (!gName || !data.gameIndex[gName]) {
        if (hint) hint.style.display = '';
        boxWins.textContent = '—';
        boxPts.textContent = '—';
        return;
      }
      if (hint) hint.style.display = 'none';
      var g = data.gameIndex[gName];
      var ptsArr = entriesSorted(g.points);
      var winsArr = entriesSorted(g.wins);

      if (winsArr.length) {
        boxWins.innerHTML = playerBtn(winsArr[0][0], true) + ' · <strong>' + winsArr[0][1] + '</strong> vittorie';
      } else boxWins.textContent = '—';

      if (ptsArr.length) {
        boxPts.innerHTML = playerBtn(ptsArr[0][0], true) + ' · <strong>' + ptsArr[0][1] + '</strong> pt';
      } else boxPts.textContent = '—';

      var top12 = ptsArr.slice(0, 12);
      var last = null, rank = 0;
      top12.forEach(function (entry, idx) {
        var name = entry[0], p = entry[1];
        if (p !== last) rank = idx + 1;
        last = p;
        var w = (g.wins && g.wins[name]) ? g.wins[name] : 0;
        var f = (g.finals && g.finals[name]) ? g.finals[name] : 0;
        var tr = document.createElement('tr');
        tr.innerHTML = '<td>' + rank + '</td><td>' + playerBtn(name, rank === 1) + '</td><td>' + (rank === 1 ? '<strong>' + p + '</strong>' : p) + '</td><td>' + w + '</td><td>' + f + '</td>';
        bodyG.appendChild(tr);
      });
    });
  }

  /* ── Interactions: Modal ── */
  function initModal(root, data) {
    var modal = root.querySelector('[data-arena-modal]');
    if (!modal) return;

    var openModal = function () { modal.classList.add('is-open'); modal.setAttribute('aria-hidden', 'false'); document.documentElement.classList.add('arenaLock'); };
    var closeModal = function () { modal.classList.remove('is-open'); modal.setAttribute('aria-hidden', 'true'); document.documentElement.classList.remove('arenaLock'); };

    root.addEventListener('click', function (e) { if (e.target.closest('[data-arena-modal-close]')) closeModal(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal(); });

    var mTitle = root.querySelector('[data-arena-modal-title]');
    var mSub = root.querySelector('[data-arena-modal-sub]');
    var mChips = root.querySelector('[data-arena-modal-chips]');
    var mWins = root.querySelector('[data-arena-m-wins]');
    var mInP = root.querySelector('[data-arena-m-inpoints]');
    var mTop8 = root.querySelector('[data-arena-m-top8]');
    var mPoints = root.querySelector('[data-arena-m-points]');
    var mAvg = root.querySelector('[data-arena-m-avg]');
    var mAdv = root.querySelector('[data-arena-m-adv]');
    var mBest = root.querySelector('[data-arena-m-bestgame]');
    var mBest2 = root.querySelector('[data-arena-m-bestgame2]');
    var mRecent = root.querySelector('[data-arena-m-recent]');

    var renderPlayer = function (rawName) {
      var name = data._canon(rawName);
      var pr = data.profile[name];
      if (!pr) return;

      var avg = pr.inPoints > 0 ? (pr.points / pr.inPoints) : 0;
      var adv = pr.inPoints > 0 ? (pr.top8 / pr.inPoints) : null;

      var bestGame = null;
      Object.entries(pr.games || {}).forEach(function (entry) {
        var g = entry[0], st = entry[1];
        var cand = Object.assign({ game: g }, st);
        if (!bestGame) bestGame = cand;
        else if (cand.points > bestGame.points || (cand.points === bestGame.points && cand.wins > bestGame.wins)) bestGame = cand;
      });

      var rec = (pr.results || []).slice().sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); }).slice(0, 8);

      if (mTitle) mTitle.textContent = pr.name;
      if (mSub) mSub.innerHTML = 'Ingressi a punti: <strong>' + pr.inPoints + '</strong> · Top 8: <strong>' + pr.top8 + '</strong> · Punti: <strong>' + pr.points + '</strong>';

      if (mChips) {
        var chips = [];
        if (pr.wins) chips.push('<span class="arena__modalChip">🏆 ' + pr.wins + ' vittorie</span>');
        if (pr.inPoints) chips.push('<span class="arena__modalChip">🎲 ' + pr.inPoints + ' a punti</span>');
        if (pr.top8) chips.push('<span class="arena__modalChip">🏅 ' + pr.top8 + ' Top 8</span>');
        if (pr.finals) chips.push('<span class="arena__modalChip">🥈 ' + pr.finals + ' finali</span>');
        mChips.innerHTML = chips.join(' ');
      }

      if (mWins) mWins.innerHTML = '<strong>' + pr.wins + '</strong>';
      if (mInP) mInP.innerHTML = '<strong>' + pr.inPoints + '</strong>';
      if (mTop8) mTop8.innerHTML = '<strong>' + pr.top8 + '</strong>';
      if (mPoints) mPoints.innerHTML = '<strong>' + pr.points + '</strong>';
      if (mAvg) mAvg.innerHTML = '<strong>' + fmtNum(avg) + '</strong>';
      if (mAdv) mAdv.innerHTML = adv === null ? '—' : '<strong>' + fmtPct(adv * 100) + '</strong>';

      if (bestGame && mBest && mBest2) {
        mBest.innerHTML = 'Gioco: <strong>' + bestGame.game + '</strong>';
        mBest2.innerHTML = 'Nel gioco: <strong>' + bestGame.points + '</strong> pt · <strong>' + bestGame.wins + '</strong> vittorie · <strong>' + bestGame.inPoints + '</strong> a punti';
      } else if (mBest) { mBest.innerHTML = '—'; if (mBest2) mBest2.innerHTML = ''; }

      if (mRecent) {
        mRecent.innerHTML = '';
        if (!rec.length) { mRecent.innerHTML = '<li class="arena__modalLi">Nessun risultato recente.</li>'; }
        else {
          rec.forEach(function (r) {
            var li = document.createElement('li');
            li.className = 'arena__modalLi';
            var g = r.game ? ' · <span class="arena__modalG">' + r.game + '</span>' : '';
            li.innerHTML = '<span class="arena__modalTag">' + r.tag + '</span> <strong>' + r.label + '</strong> · ' + r.pts + ' pt' + g + ' <span class="arena__modalT">' + r.title + '</span> <span class="arena__modalD">' + r.date + '</span>';
            mRecent.appendChild(li);
          });
        }
      }
      openModal();
    };

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-arena-player]');
      if (!btn) return;
      renderPlayer(btn.getAttribute('data-arena-player') || btn.textContent || '');
    });

    return renderPlayer;
  }

  /* ── Interactions: Search ── */
  function initSearch(root, data, renderPlayer) {
    var inp = root.querySelector('[data-arena-player-search]');
    if (!inp) return;
    var timer = null;

    inp.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var q = (inp.value || '').trim();
        if (!q) return;
        var all = Object.keys(data.profile);
        var hit = all.find(function (n) { return n.toLowerCase() === q.toLowerCase(); }) ||
                  all.find(function (n) { return n.toLowerCase().includes(q.toLowerCase()); });
        if (hit && renderPlayer) renderPlayer(hit);
      }
    });

    inp.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var q = (inp.value || '').trim().toLowerCase();
        var table = root.querySelector('[data-arena-table]');
        if (!table) return;
        Array.from(table.querySelectorAll('tbody tr')).forEach(function (tr) {
          if (!q) { tr.classList.remove('is-dim'); return; }
          tr.classList.toggle('is-dim', !(tr.textContent || '').toLowerCase().includes(q));
        });
      }, 120);
    });
  }

  /* ── Interactions: Collapsibles ── */
  function initCollapsibles(root) {
    root.querySelectorAll('[data-arena-toggle]').forEach(function (btn) {
      var targetId = btn.getAttribute('data-arena-toggle');
      var target = root.querySelector('[data-arena-collapse="' + targetId + '"]');
      if (!target) return;

      btn.addEventListener('click', function () {
        var isOpen = btn.classList.contains('is-open');
        if (isOpen) {
          target.style.maxHeight = '0';
          target.classList.remove('is-open');
          btn.classList.remove('is-open');
        } else {
          target.style.maxHeight = target.scrollHeight + 'px';
          target.classList.add('is-open');
          btn.classList.add('is-open');
        }
      });
    });
  }

  /* ── Interactions: Sort visible tournaments ── */
  function sortTournaments(root) {
    var list = root.querySelector('[data-arena-tourney-list]');
    if (!list) return;
    var cards = Array.from(list.children);
    cards.sort(function (a, b) {
      var da = (a.dataset.date || '').trim();
      var db = (b.dataset.date || '').trim();
      if (da && db) return db.localeCompare(da);
      if (db) return 1;
      if (da) return -1;
      return 0;
    });
    cards.forEach(function (c) { list.appendChild(c); });
  }

  /* ═══════════════════════════════════════════════════════
     LEAGUE PAGE INIT
     ═══════════════════════════════════════════════════════ */
  function initLeague() {
    var root = document.querySelector('[data-arena]');
    if (!root) return;

    var league = root.dataset.league || 'light';
    var isGlobal = root.dataset.global === 'true';
    var recentN = parseInt(root.dataset.recentN) || 3;
    var minInPoints = parseInt(root.dataset.minInpoints) || 2;
    var pts;
    try { pts = JSON.parse(root.dataset.pts || '{}'); } catch (e) { pts = {}; }
    pts = { w: pts.w || 25, f: pts.f || 20, s: pts.s || 16, q: pts.q || 9, o: pts.o || 4 };
    var alias = buildAlias((root.dataset.aliasMap || '').replace(/\\n/g, '\n'));
    var canon = makeCanon(alias);

    var items = parseTournaments(root, canon, pts, league, isGlobal);
    var data = buildProfiles(items, canon, pts);
    data._canon = canon;

    var totalSorted = entriesSorted(data.pointsTotal);
    var maxPts = totalSorted.length ? totalSorted[0][1] : 0;

    renderPodium(root, totalSorted);
    renderLeaderboard(root, totalSorted, maxPts);

    var cfg = { recentN: recentN, minInPoints: minInPoints, _items: items, _canon: canon, _pts: pts };
    renderStats(root, data, cfg);
    renderGameStats(root, data);

    var renderPlayer = initModal(root, data);
    initSearch(root, data, renderPlayer);
    initCollapsibles(root);
    sortTournaments(root);
  }

  /* ═══════════════════════════════════════════════════════
     HUB PAGE INIT
     ═══════════════════════════════════════════════════════ */
  function initHub() {
    var root = document.querySelector('[data-arena-hub]');
    if (!root) return;

    var pts;
    try { pts = JSON.parse(root.dataset.pts || '{}'); } catch (e) { pts = {}; }
    pts = { w: pts.w || 25, f: pts.f || 20, s: pts.s || 16, q: pts.q || 9, o: pts.o || 4 };
    var alias = buildAlias((root.dataset.aliasMap || '').replace(/\\n/g, '\n'));
    var canon = makeCanon(alias);

    var leagues = ['light', 'mid', 'heavy'];
    var globalPoints = {};

    leagues.forEach(function (lg) {
      var cards = Array.from(root.querySelectorAll('[data-arena-tournament][data-league="' + lg + '"]'));
      var count = cards.length;
      var leaguePoints = {};

      cards.forEach(function (el) {
        var winner = canon(el.dataset.winner || '');
        var finalist = canon(el.dataset.finalist || '');
        var semiList = parseList(el.dataset.semi).map(canon);
        var qList = parseList(el.dataset.quarti).map(canon);
        var oList = parseList(el.dataset.ottavi).map(canon);

        var addP = function (map, name, delta) { var k = canon(name); if (!k) return; map[k] = (map[k] || 0) + delta; };
        addP(leaguePoints, winner, pts.w); addP(globalPoints, winner, pts.w);
        addP(leaguePoints, finalist, pts.f); addP(globalPoints, finalist, pts.f);
        semiList.forEach(function (n) { addP(leaguePoints, n, pts.s); addP(globalPoints, n, pts.s); });
        qList.forEach(function (n) { addP(leaguePoints, n, pts.q); addP(globalPoints, n, pts.q); });
        oList.forEach(function (n) { addP(leaguePoints, n, pts.o); addP(globalPoints, n, pts.o); });
      });

      var sorted = entriesSorted(leaguePoints);
      var leader = sorted.length ? sorted[0] : null;

      var countEl = root.querySelector('[data-hub-count-' + lg + ']');
      var leaderEl = root.querySelector('[data-hub-leader-' + lg + ']');
      var playersEl = root.querySelector('[data-hub-players-' + lg + ']');

      if (countEl) countEl.textContent = count;
      if (leaderEl) leaderEl.textContent = leader ? leader[0] + ' (' + leader[1] + ' pt)' : '—';
      if (playersEl) playersEl.textContent = Object.keys(leaguePoints).length;
    });

    /* Global top 5 */
    var globalBody = root.querySelector('[data-hub-global-body]');
    if (globalBody) {
      globalBody.innerHTML = '';
      var globalSorted = entriesSorted(globalPoints);
      var top5 = globalSorted.slice(0, 5);
      var last = null, rank = 0;
      top5.forEach(function (entry, idx) {
        var name = entry[0], p = entry[1];
        if (p !== last) rank = idx + 1;
        last = p;
        var tr = document.createElement('tr');
        var posClass = 'arena__pos';
        if (rank <= 3) posClass += ' arena__pos--' + rank;
        tr.innerHTML = '<td><span class="' + posClass + '">' + rank + '</span></td><td style="font-weight:700">' + name + '</td><td style="font-weight:700;color:var(--a-accent)">' + p + ' pt</td>';
        globalBody.appendChild(tr);
      });
    }

    /* Hub stats */
    var totalTourneys = root.querySelectorAll('[data-arena-tournament]').length;
    var totalPlayers = Object.keys(globalPoints).length;
    var totalEl = root.querySelector('[data-hub-total-tournaments]');
    var playersEl = root.querySelector('[data-hub-total-players]');
    if (totalEl) totalEl.textContent = totalTourneys;
    if (playersEl) playersEl.textContent = totalPlayers;
  }

  /* ── Boot ── */
  function boot() {
    initLeague();
    initHub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
